import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import '../styles/app.css';

/** Authenticated app frame: sidebar + topbar + routed content. */
export function AppShell() {
  // Below the 900px "nav" breakpoint the sidebar leaves the grid and becomes an
  // off-canvas drawer. Above it the sidebar is always visible and this state is
  // inert (the toggle that sets it is display:none).
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const closeNav = useCallback(() => setNavOpen(false), []);

  // Route changes close the drawer. Covers NavLink taps and browser back/forward.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!navOpen) return;

    // Move focus into the drawer so the keyboard lands where the eye does, and
    // return it to the toggle on close.
    const toggle = toggleRef.current;
    navRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    // Lock the page behind the drawer so a touch scroll moves the nav, not the
    // dashboard underneath it.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      toggle?.focus();
    };
  }, [navOpen]);

  return (
    <div className="app">
      <div
        className={'drawer-backdrop nav-backdrop no-print' + (navOpen ? ' open' : '')}
        onClick={closeNav}
        aria-hidden="true"
      />
      <Sidebar ref={navRef} open={navOpen} onNavigate={closeNav} />
      <main className="main">
        <Topbar ref={toggleRef} navOpen={navOpen} onMenuClick={() => setNavOpen((v) => !v)} />
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
